// Keyboard shortcuts. Single keys only act when you are not typing (an input, a text area, a select, or anything
// editable has focus) and no dialog is open; with a modifier they always act.
import { useEffect, useRef } from 'react';

export const isTyping = (t: EventTarget | null) => {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
};

/** Is a modal (the command palette, a sheet) open? Page shortcuts stand down while one is. */
const modalOpen = () => !!document.querySelector('[aria-modal="true"]');

/** Map of key to handler, e.g. { j: next, k: prev, a: approve }. Handlers see the event. */
export function useHotkeys(map: Record<string, (e: KeyboardEvent) => void>, enabled = true) {
  const ref = useRef(map);
  ref.current = map;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.metaKey || e.ctrlKey) return;
      if (isTyping(e.target) || modalOpen()) return;
      const fn = ref.current[e.key];
      if (fn) { e.preventDefault(); fn(e); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
}

/** "⌘K" on a Mac, "Ctrl K" elsewhere. */
export const modKey = () => (typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl ');
